import {type Ref, type ShallowRef, shallowRef} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLoadingData} from '@/ts/composables/pure/use-loading-data-ref';
import type {GetTodayResponse} from '@/ts/types/dto/today-dto';

interface TodayPayloadData {
  payload: ShallowRef<GetTodayResponse | null>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  load(): Promise<void>;
}

/**
 * The page's one request. Every tile of the bento reads from what this returns, so the whole page
 * has a single loading state and a single failure — which is also how the backend treats it: a
 * content piece with no translation in the user's language fails the request outright rather than
 * serving a mute card, on purpose.
 *
 * The timezone is read once, here, and passed on every call. The backend resolves the greeting, the
 * all-caught-up state that expires with the user's day, the promo of the day and the three-day
 * content window against it, so a page that did not send it would be told about somebody else's day.
 *
 * WHY THE REQUEST IS NOT FIRED BY THIS COMPOSABLE, unlike the `useLoadingDataRefNull` most pages
 * use: that helper fetches in its own `onBeforeMount`, which runs whether or not the page is going
 * to be shown. This page redirects users whose licence does not reach it, and GET /me/today is not
 * a plain read — it fixes the content pair for the window and can mark an expired challenge as
 * failed — so firing it for someone on their way out would write to their state for a page they
 * never see. The page calls `load` once it knows it is staying, which is what the journal and
 * program tiles already do with their own fetches.
 *
 * Errors are surfaced rather than notified, and deliberately: with no payload there is no page to
 * put a toast on top of. `useLoadingData` translates them and passes the consent and licence codes
 * through untouched, which is what lets `loading-error` redirect on those instead of drawing a box.
 */
export function useTodayPayload(): TodayPayloadData {
  const api = useApi();
  const timezone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const payload = shallowRef<GetTodayResponse | null>(null);

  const [load, loading, error] = useLoadingData(
    payload,
    async() => api.me.getToday(timezone),
  );

  return {payload, loading, error, load};
}
